//Need to be removed

/**
 * @description A Map of all recognized country calling codes, mapped to an object containing the
 * country name and, optionally, regular expression defining a valid phone number pattern,
 * and standardized formatting for any phone number that matches the pattern
 * @type {!Map<string, {
 *  name:(!string),
 *  pattern:(!RegExp|undefined),
 *  format:(!string|undefined)
 * }>}
 */
const COUNTRY_CODES = new Map([
  ['1', {name: 'US/Canada', pattern: /^\+?1?[-\s]?\(?([\dX]{3})[)-]?\s?(\d{3})-?(\d{4})$/, format: '($1) $2-$3'}],
  ['44', {name: 'United Kingdom'}],
]);
/**
 * @description The `PhoneUtil` class provides various functions related to formatting phone numbers in a standard way
 */
const PhoneUtil = {
  /**
   * @description Returns a Map of all recognized country calling codes
   * @return {Map}
   */
  get countryCodes() {
    return COUNTRY_CODES;
  },
  /**
   * @description Format phone number using format from country code Map--if no format is specified,
   * only the digits will be returned
   * @param {!string|null=} phoneNumber
   * @param {!string=} countryCode
   * @return {!string}
   */
  formatPhone(phoneNumber = '', countryCode = '1') {
    const numbersOnly = phoneNumber.replace(/\D/g, '');
    const format = PhoneUtil.getFormat(countryCode);
    if (!format) {
      return numbersOnly;
    }
    const pattern = PhoneUtil.getPattern(countryCode); // .replace(/\)/g, ')?');
    let formattedPhone;
    if (!numbersOnly) {
      formattedPhone = '';
    } else {
      formattedPhone = numbersOnly.replace(new RegExp(pattern), format);
    }
    return formattedPhone;
  },
  /**
   * @description Format a string as a phone number if valid (US/Canada only); otherwise, apply no special formatting
   * @param {!string} newValue
   * @param {!string=} oldValue
   * @return {!string}
   */
  maybeFormatPhone(newValue, oldValue = '') {
    if (PhoneUtil.isValidNumber(newValue)) { // valid US/Canada phone number? format phone
      const formattedPhone = PhoneUtil.formatPhone(newValue);
      if (formattedPhone) {
        return formattedPhone;
      }
    } else if (/\D/.test(newValue)) { // any non-digits
      const alreadyFormattedNumber = /\([0-9]{3}\) [0-9]{3}-[0-9]{4}/;
      if (alreadyFormattedNumber.test(oldValue)) {
        return newValue.replace(/[() -]/g, ''); // already-formatted phone number => strip formatting characters (##########@)
      }
    }
    // otherwise, leave it as-is
    return newValue;
  },
  /**
   * @description Retrieve country name from Map, given the country code
   * @param {!string} countryCode
   * @return {!string}
   */
  getCountryName(countryCode) {
    const country = COUNTRY_CODES.get(countryCode);
    return (country && country.name) || '';
  },
  /**
   * @description Get matching pattern for phone input
   * @param {!string} countryCode
   * @return {!string}
   */
  getPattern(countryCode) {
    const country = COUNTRY_CODES.get(countryCode);
    return (country && country.pattern && country.pattern.source) || `^\\d+$`;
  },
  /**
   * @description Get standardized format for valid phone input (coincides with pattern and its capture groups)
   * @param {!string} countryCode
   * @return {!string}
   */
  getFormat(countryCode) {
    const country = COUNTRY_CODES.get(countryCode);
    return (country && country.format) || '';
  },
  /**
   * @description mask a phone number
   * @param {string|undefined} phoneNumber
   * @param {string} [char='•'] - character used to mask the number
   * @return {string}
   */
  phoneMask(phoneNumber, char = '•') {
    if (!phoneNumber) {
      return '';
    }
    // '+1-XXX-XXX-XX12' => '•••-•••-••12'
    // '+1-123-233-4412' => '•••-•••-••12'
    return `${char}${char}${char}-${char}${char}${char}-${char}${char}${phoneNumber.slice(-2)}`;
  },
  /**
   * @description test phone number validity
   * @param {?string} phoneNumber
   * @return {boolean}
   */
  isValidNumber(phoneNumber, countryCode = '1') {
    if (/[a-zA-Z@]/.test(phoneNumber)) {
      return false;
    }  
    phoneNumber = PhoneUtil.cleanPhoneNumber(phoneNumber);
    const country = COUNTRY_CODES.get(countryCode);
    const regex = country && country.pattern;
    return Boolean(regex) && regex.test(phoneNumber);
  },
  /**
   * @description return phone number stripped of non-numeric and common formatting characters (and a few uncommon, unlikely ones)
   * @param {?string} phoneNumber
   * @return {string}
   */
  cleanPhoneNumber(phoneNumber = '') {
    return phoneNumber.replace(/[\D -().+,[\]|*_]/g, '');  
  },
  /**
   * Takes a phone number, formatted in any way, and returns it in E164 format (+15555555555)
   * @param {!string} phoneNumber
   * @param {string=} countryCode
   * @return {!string}
   */
  toE164(phoneNumber, countryCode = '1') {
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    switch (countryCode) {
      case '1':
        if (/^[0-9]{10}$/.test(digitsOnly)) {
          return `+${countryCode}${digitsOnly}`; // if countryCode 1 and phone is only 10 digits, append country code
        }
        break;
      default:
        break;
    }
    if (digitsOnly.startsWith(countryCode)) { // countryCode is already appended, so just append a `+`
      return `+${digitsOnly}`;
    }
    return `+${countryCode}${digitsOnly}`;
  },
};
export default PhoneUtil;
